import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import moment from "moment/min/moment-with-locales";
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  Megaphone,
  RefreshCw,
  TicketPercent,
  TrendingUp,
  Users,
  Wallet,
} from "lucide-react";
import { useBusinessDashboard } from "@/hooks/useBusinessDashboard";
import {
  GetBusinessPlanAPI,
  type BusinessPlanResult,
} from "@/services/api/business/getBusinessPlan";
import {
  formatMoney,
  type BusinessStatementFeeLine,
} from "@/utils/businessStatement";

type SummaryCard = {
  key: string;
  label: string;
  value: number;
  accent: boolean;
  sub?: React.ReactNode;
};

/**
 * Business dashboard — monthly statement summary (platform fee), portfolio
 * counters and the per-coupon breakdown returned by
 * `GET /api/business/monthlyStatement/getMonthlyStatement`.
 */
const BusinessDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const business = useSelector((state: any) => state.auth.business);
  const [month, setMonth] = useState(() => moment().startOf("month"));
  const [plan, setPlan] = useState<BusinessPlanResult | null>(null);

  const {
    items,
    totals,
    refreshing,
    couponCount,
    sponsorCount,
    reach,
    reachLoading,
    refresh,
  } = useBusinessDashboard(month);

  // The plan is read once on mount and again on every manual refresh, so the
  // fee card keeps matching what the business is actually billed.
  const loadPlan = useCallback(async () => {
    const resp = await GetBusinessPlanAPI();
    setPlan(resp?.status === 0 ? (resp.result ?? null) : null);
  }, []);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const currentMonth = moment().startOf("month");
  const canGoNext = month.isBefore(currentMonth, "month");
  const startMonth = plan?.subscription_started_at
    ? moment(plan.subscription_started_at).startOf("month")
    : null;
  const canGoPrev = startMonth ? month.isAfter(startMonth, "month") : true;

  // The selector never reaches months before the subscription started —
  // clamps too, for a plan (or a start date) that resolves after navigation.
  useEffect(() => {
    if (startMonth && month.isBefore(startMonth, "month")) {
      setMonth(startMonth);
    }
  }, [startMonth, month]);

  // Plan status under the platform-fee card, with the "Manage" shortcut to the
  // billing section for changing or cancelling it.
  const planSub = useMemo(() => {
    const [status, notes] = plan ? describePlan(plan, t) : ["", ""];

    return (
      <div className="mt-1 space-y-0.5">
        {status ? <p className="text-xs text-gray-500">{status}</p> : null}
        {notes ? <p className="text-xs text-gray-400">{notes}</p> : null}
        <Link
          to="/portal/account"
          className="inline-block text-xs font-semibold text-[#ffa217] hover:underline"
        >
          {t("Manage")}
        </Link>
      </div>
    );
  }, [plan, t]);

  // Itemized "what you're paying for" list under the Total Fees value: one row
  // per fee line with a short reason, so the number is never unexplained.
  const totalFeesSub = useMemo(
    () => (
      <div className="mt-2 space-y-1">
        <p className="text-xs font-semibold text-gray-500">
          {t("What you're paying for")}
        </p>
        {totals.fees.map((line) => {
          const info = describeFeeLine(line, t);
          return (
            <div key={line.code} className="text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="text-gray-500">{info.label}</span>
                <span className="font-semibold text-gray-700">
                  {formatMoney(line.amount, totals.currency)}
                </span>
              </div>
              {info.reason ? (
                <p className="text-gray-400">{info.reason}</p>
              ) : null}
            </div>
          );
        })}
      </div>
    ),
    [t, totals.currency, totals.fees],
  );

  const cards: SummaryCard[] = [
    {
      key: "platformFee",
      label: "Platform Fee",
      value: totals.platformFee,
      accent: true,
      sub: planSub,
    },
    {
      key: "totalFees",
      label: "Total Fees",
      value: totals.totalFees,
      accent: false,
      sub: totalFeesSub,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#333]">{t("Dashboard")}</h1>
          <p className="mt-1 text-sm text-gray-500">
            {business?.company_name || business?.company_email || ""}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-2xl border border-gray-200 bg-white p-1">
            <button
              type="button"
              disabled={!canGoPrev}
              onClick={() => setMonth((value) => value.clone().subtract(1, "month"))}
              aria-label={t("Previous Month")}
              className="rounded-xl px-2 py-1.5 text-gray-500 transition hover:bg-gray-50 hover:text-[#ffa217] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="min-w-[120px] text-center text-sm font-semibold text-gray-700">
              {month.format("MMMM YYYY")}
            </span>
            <button
              type="button"
              disabled={!canGoNext}
              onClick={() => setMonth((value) => value.clone().add(1, "month"))}
              aria-label={t("Next Month")}
              className="rounded-xl px-2 py-1.5 text-gray-500 transition hover:bg-gray-50 hover:text-[#ffa217] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <button
            type="button"
            onClick={() => {
              loadPlan();
              refresh();
            }}
            disabled={refreshing}
            aria-label={t("Refresh")}
            className="inline-flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-600 transition hover:border-[#ffa217] hover:text-[#ffa217] disabled:opacity-50"
          >
            <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {cards.map((card) => (
          <div
            key={card.key}
            className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {t(card.label)}
            </p>
            <p
              className={`mt-2 text-xl font-bold ${
                card.accent ? "text-[#ffa217]" : "text-gray-900"
              }`}
            >
              {formatMoney(card.value, totals.currency)}
            </p>
            {card.sub}
          </div>
        ))}
      </section>

      <section className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          icon={<TicketPercent size={16} />}
          label={t("Coupons")}
          value={String(couponCount)}
          to="/portal/coupons"
        />
        <StatCard
          icon={<Users size={16} />}
          label={t("Sponsors")}
          value={String(sponsorCount)}
          to="/portal/sponsors"
        />
        <StatCard
          icon={<TrendingUp size={16} />}
          label={t("redeemed")}
          value={String(items.reduce((sum, item) => sum + toCount(item.redeemed_count), 0))}
        />
        <StatCard
          icon={<Wallet size={16} />}
          label={t("Used")}
          value={String(items.reduce((sum, item) => sum + toCount(item.used_count), 0))}
        />
      </section>

      <section className="rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <h2 className="text-base font-bold text-gray-900">
            {t("Reach & Engagement")}
          </h2>
          <span className="text-xs text-gray-400">
            {month.format("MMMM YYYY")}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 p-5 lg:grid-cols-4">
          <MetricCard
            icon={<Eye size={16} />}
            label={t("Coupon Reads")}
            value={toCount(reach?.summary?.coupon_readers)}
            sub={`${t("Reads")}: ${toCount(reach?.summary?.coupon_reads) || "-"}`}
          />
          <MetricCard
            icon={<TicketPercent size={16} />}
            label={t("Coupon Redeemed")}
            value={toCount(reach?.summary?.coupon_redeemed)}
            sub={`${t("Used")}: ${toCount(reach?.summary?.coupon_used) || "-"}`}
          />
          <MetricCard
            icon={<Megaphone size={16} />}
            label={t("Sponsor Reads")}
            value={toCount(reach?.summary?.sponsor_readers)}
            sub={`${t("Reads")}: ${toCount(reach?.summary?.sponsor_reads) || "-"}`}
          />
          <MetricCard
            icon={<Users size={16} />}
            label={t("Sponsor Redeemed")}
            value={toCount(reach?.summary?.sponsor_redeemed)}
            sub={`${t("Readers")}: ${toCount(reach?.summary?.sponsor_students) || "-"}`}
          />
        </div>

        {reachLoading ? (
          <p className="px-5 pb-8 text-center text-sm text-gray-400">
            {t("loading")}
          </p>
        ) : null}
      </section>
    </div>
  );
};

const toCount = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Plan status for the platform-fee card, as [headline, notes]: what the
 * business is on (or the free first year it was granted), then the queued
 * plan change and any scheduled cancellation. Both are empty when the plan
 * says nothing worth showing.
 */
const describePlan = (plan: BusinessPlanResult, t: TFunction): [string, string] => {
  // First 100 businesses ride out a free first year but still hold a real
  // plan underneath, so the rate and renewal dates go into the notes rather
  // than only living on the account page.
  if (plan.is_promo && plan.free_trial_end) {
    const notes = [describePlanPrice(plan, t)];
    if (plan.renews_on) {
      notes.push(
        t("Renews on {{date}}", {
          date: moment(plan.renews_on).format("DD MMM YYYY"),
        }),
      );
    }
    if (plan.minimum_term_end) {
      notes.push(
        t("Minimum term ends {{date}}", {
          date: moment(plan.minimum_term_end).format("DD MMM YYYY"),
        }),
      );
    }
    const pending = describePendingPlan(plan, t);
    if (pending) notes.push(pending);
    const cancelled = describeCancellation(plan, t);
    if (cancelled) notes.push(cancelled);

    return [
      `${t("Free first year (First 100)")} · ${t("until {{date}}", {
        date: moment(plan.free_trial_end).format("DD MMM YYYY"),
      })}`,
      notes.join(" · "),
    ];
  }

  if (!plan.plan_type) return ["", ""];

  const notes: string[] = [];
  const pending = describePendingPlan(plan, t);
  if (pending) notes.push(pending);
  const cancelled = describeCancellation(plan, t);
  if (cancelled) {
    notes.push(cancelled);
  } else if (plan.minimum_term_end) {
    notes.push(
      t("Minimum term ends {{date}}", {
        date: moment(plan.minimum_term_end).format("DD MMM YYYY"),
      }),
    );
  }

  return [describePlanPrice(plan, t), notes.join(" · ")];
};

/**
 * "Yearly plan · HKD 500/year" — the plan type plus the rate the statement
 * bills, shared by the promo and non-promo plan-status lines.
 */
const describePlanPrice = (plan: BusinessPlanResult, t: TFunction): string => {
  const yearly = plan.plan_type === "yearly";
  const fee = yearly
    ? plan.yearly_fee ?? plan.yearly_price ?? 500
    : plan.monthly_fee ?? plan.monthly_price ?? 400;

  // The yearly rate covers a whole year, so the period follows the plan type.
  const period = t(yearly ? "year" : "month");

  return `${t(yearly ? "Yearly plan" : "Monthly plan")} · HKD ${toCount(fee)}/${period}`;
};

/** Queued plan change ("Yearly from next renewal"), empty when there is none. */
const describePendingPlan = (plan: BusinessPlanResult, t: TFunction): string => {
  if (!plan.pending_plan_type || plan.pending_plan_type === plan.plan_type) {
    return "";
  }
  return `${
    plan.pending_plan_type === "yearly" ? t("Yearly") : t("Monthly")
  } ${t("from next renewal")}`;
};

/** Scheduled-cancellation note, empty when the plan simply renews. */
const describeCancellation = (plan: BusinessPlanResult, t: TFunction): string => {
  if (plan.cancel_at) {
    return t("Auto-renewal cancelled — plan ends on {{date}}", {
      date: moment(plan.cancel_at).format("DD MMM YYYY"),
    });
  }
  if (plan.cancel_at_period_end) {
    return plan.renews_on
      ? `${t("Renewal cancelled")} · ${t("until {{date}}", {
          date: moment(plan.renews_on).format("DD MMM YYYY"),
        })}`
      : t("Renewal cancelled");
  }
  return "";
};

/**
 * One-line reason for a fee line on the Total Fees card, so the business sees
 * what it is paying for (and why a pending month shows nothing yet).
 */
const describeFeeLine = (
  line: BusinessStatementFeeLine,
  t: TFunction,
): { label: string; reason: string } => {
  const meta = line.meta ?? {};
  if (line.code === "platform_fee") {
    return {
      label: t("Platform Fee"),
      reason: line.amount === 0 ? t("Platform fee (free first year)") : "",
    };
  }
  if (line.code === "coupon_shortfall") {
    if (meta.status === "pending") {
      return {
        label: t("Coupon Shortfall"),
        reason: t("No charge yet — assessed after the month ends"),
      };
    }
    const count = Number(meta.coupon_new_count ?? 0);
    const required = Number(meta.required ?? 1);
    if (meta.charged_year && meta.charged_month) {
      const charged = moment({
        year: Number(meta.charged_year),
        month: Number(meta.charged_month) - 1,
      });
      return {
        label: t("Coupon Shortfall"),
        reason: t("{{count}} of {{required}} new coupon required — charged in {{month}}", {
          count,
          required,
          month: charged.format("MMMM YYYY"),
        }),
      };
    }
    return {
      label: t("Coupon Shortfall"),
      reason: t("{{count}} of required new coupon published", { count }),
    };
  }
  return { label: line.code, reason: "" };
};

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
}> = ({ icon, label, value, sub }) => {
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        <span className="text-[#ffa217]">{icon}</span>
        {label}
      </div>
      <p className="mt-2 text-xl font-bold text-gray-900">{value}</p>
      <p className="mt-0.5 text-xs text-gray-400">{sub}</p>
    </div>
  );
};

const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  to?: string;
}> = ({ icon, label, value, to }) => {
  const body = (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
        <span className="text-[#ffa217]">{icon}</span>
        {label}
      </div>
      <p className="mt-2 text-xl font-bold text-gray-900">{value}</p>
    </div>
  );

  if (!to) return body;
  return (
    <Link to={to} className="block">
      {body}
    </Link>
  );
};

export default BusinessDashboardPage;
